import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, api } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Calendar } from "../components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { CalendarIcon, ArrowRight, Compass, Check } from "lucide-react";
import { format } from "date-fns";
import { toast } from "sonner";

const focusOptions = [
  { id: "general", label: "General Life", icon: "🌟" },
  { id: "career", label: "Career", icon: "💼" },
  { id: "relationships", label: "Relationships", icon: "❤️" },
  { id: "finance", label: "Finance", icon: "💰" },
  { id: "health", label: "Health", icon: "🧘" },
  { id: "spirituality", label: "Spirituality", icon: "✨" },
];

const Onboarding = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [firstName, setFirstName] = useState(user?.first_name || user?.name?.split(" ")[0] || "");
  const [dateOfBirth, setDateOfBirth] = useState(null);
  const [selectedFocusAreas, setSelectedFocusAreas] = useState([]);
  const [notificationTime, setNotificationTime] = useState("07:00");

  const toggleFocusArea = (id) => {
    setSelectedFocusAreas((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const handleComplete = async () => {
    if (!firstName.trim()) {
      toast.error("Please enter your name");
      return;
    }
    if (!dateOfBirth) {
      toast.error("Please select your date of birth");
      return;
    }
    if (selectedFocusAreas.length === 0) {
      toast.error("Please select at least one focus area");
      return;
    }

    setLoading(true);
    try {
      const response = await api.post("/user/onboarding", {
        first_name: firstName,
        date_of_birth: format(dateOfBirth, "yyyy-MM-dd"),
        focus_areas: selectedFocusAreas,
        notification_time: notificationTime,
      });
      setUser(response.data);
      toast.success("Welcome to SoulGuide!");
      navigate("/dashboard", { replace: true });
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen relative" style={{ background: "#070B14" }}>
      {/* Background Pattern */}
      <div
        className="absolute inset-0 opacity-10"
        style={{
          backgroundImage:
            "url(https://images.pexels.com/photos/21031387/pexels-photo-21031387.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940)",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-b from-[#070B14]/90 to-[#070B14]" />

      <div className="relative z-10 min-h-screen flex flex-col">
        {/* Header */}
        <header className="p-6 md:p-8">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#D4AF37] to-[#A16207] flex items-center justify-center">
              <Compass className="w-5 h-5 text-[#070B14]" />
            </div>
            <span className="font-display text-2xl text-white">SoulGuide</span>
          </div>
        </header>

        {/* Progress Indicator */}
        <div className="px-6 md:px-8 py-4">
          <div className="max-w-md mx-auto flex gap-2">
            {[1, 2, 3].map((s) => (
              <div
                key={s}
                className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                  s <= step ? "bg-[#D4AF37]" : "bg-white/10"
                }`}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <main className="flex-1 flex items-center justify-center px-6 py-8">
          <div className="w-full max-w-md">
            {/* Step 1: Name */}
            {step === 1 && (
              <div className="glass-card p-8 space-y-6 animate-fade-in-up" data-testid="onboarding-step-1">
                <div className="text-center space-y-2">
                  <h2 className="font-display text-3xl text-white">Welcome to SoulGuide</h2>
                  <p className="text-white/60 font-body">Let's personalise your guidance</p>
                </div>

                <div className="space-y-4">
                  <label className="block text-sm text-white/70 font-body">What should we call you?</label>
                  <Input
                    data-testid="first-name-input"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder="Enter your name"
                    className="custom-input w-full text-lg"
                  />
                </div>

                <Button
                  data-testid="step-1-next"
                  onClick={() => firstName.trim() && setStep(2)}
                  disabled={!firstName.trim()}
                  className="btn-gold w-full flex items-center justify-center gap-2"
                >
                  Continue <ArrowRight className="w-5 h-5" />
                </Button>
              </div>
            )}

            {/* Step 2: Date of Birth */}
            {step === 2 && (
              <div className="glass-card p-8 space-y-6 animate-fade-in-up" data-testid="onboarding-step-2">
                <div className="text-center space-y-2">
                  <h2 className="font-display text-3xl text-white">When were you born?</h2>
                  <p className="text-white/60 font-body">This helps us personalise your readings</p>
                </div>

                <div className="space-y-4">
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        data-testid="dob-picker-trigger"
                        variant="outline"
                        className="custom-input w-full justify-start text-left font-normal"
                      >
                        <CalendarIcon className="mr-2 h-4 w-4 text-[#D4AF37]" />
                        {dateOfBirth ? (
                          format(dateOfBirth, "PPP")
                        ) : (
                          <span className="text-white/30">Select your date of birth</span>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0 bg-[#0A101D] border-white/10" align="center">
                      <Calendar
                        data-testid="dob-calendar"
                        mode="single"
                        selected={dateOfBirth}
                        onSelect={setDateOfBirth}
                        disabled={(date) => date > new Date() || date < new Date("1900-01-01")}
                        initialFocus
                        captionLayout="dropdown-buttons"
                        fromYear={1940}
                        toYear={new Date().getFullYear()}
                        className="bg-[#0A101D] text-white"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(1)}
                    className="btn-secondary flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    data-testid="step-2-next"
                    onClick={() => dateOfBirth && setStep(3)}
                    disabled={!dateOfBirth}
                    className="btn-gold flex-1 flex items-center justify-center gap-2"
                  >
                    Continue <ArrowRight className="w-5 h-5" />
                  </Button>
                </div>
              </div>
            )}

            {/* Step 3: Focus Areas */}
            {step === 3 && (
              <div className="glass-card p-8 space-y-6 animate-fade-in-up" data-testid="onboarding-step-3">
                <div className="text-center space-y-2">
                  <h2 className="font-display text-3xl text-white">What guides your focus?</h2>
                  <p className="text-white/60 font-body">Select areas you'd like guidance on</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  {focusOptions.map((option) => (
                    <button
                      key={option.id}
                      data-testid={`focus-${option.id}`}
                      onClick={() => toggleFocusArea(option.id)}
                      className={`p-4 rounded-xl border transition-all duration-300 text-left ${
                        selectedFocusAreas.includes(option.id)
                          ? "focus-chip-selected border-[#D4AF37]"
                          : "border-white/10 bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-2xl">{option.icon}</span>
                        <span className="font-body text-sm text-white">{option.label}</span>
                        {selectedFocusAreas.includes(option.id) && (
                          <Check className="w-4 h-4 text-[#D4AF37] ml-auto" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>

                <div className="space-y-3">
                  <label className="block text-sm text-white/70 font-body">
                    Preferred notification time
                  </label>
                  <Input
                    data-testid="notification-time-input"
                    type="time"
                    value={notificationTime}
                    onChange={(e) => setNotificationTime(e.target.value)}
                    className="custom-input w-full"
                  />
                </div>

                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    onClick={() => setStep(2)}
                    className="btn-secondary flex-1"
                  >
                    Back
                  </Button>
                  <Button
                    data-testid="complete-onboarding"
                    onClick={handleComplete}
                    disabled={loading || selectedFocusAreas.length === 0}
                    className="btn-gold flex-1"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-[#070B14] border-t-transparent rounded-full animate-spin" />
                        Setting up...
                      </span>
                    ) : (
                      "Start My Journey"
                    )}
                  </Button>
                </div>
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default Onboarding;
